/**
 * Which columns the option chain draws — ROADMAP 4.20.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE VIEWS OF ONE LADDER, AND THEY ANSWER DIFFERENT QUESTIONS
 *
 *   MARKET    where the crowd is. Open interest and the chance of finishing
 *             in the money, beside delta, IV and the price.
 *   GREEKS    what the position will DO. The four risks replace OI and ITM%,
 *             because nobody reads both at once and the row is only so wide.
 *   COMBINED  what a straddle at this strike costs. Delta, IV and the price
 *             on each side and nothing else, so the eye has one number per
 *             column to travel down, and a COMBINED section carrying the two
 *             premiums added together with one pair of buttons for both legs.
 *
 * It was a two-state toggle, which is why this is a union rather than a
 * boolean: a third view would have become `showGreeks && !showCombined` at
 * five call sites, and the fourth would have been unwritable.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/*
 * THE VIEW IS NOT REMEMBERED, AND THAT IS DELIBERATE.
 *
 * It used to persist to `localStorage`, which meant a trader who had
 * glanced at the four risks once opened the terminal in Greeks the next
 * morning — a ladder with no open interest and no price ladder on it — and
 * had to work out what had happened before they could work out the market.
 *
 * Every session starts in MARKET. Switching is one click and costs nothing;
 * starting in a view you did not choose costs the first thirty seconds of a
 * session, every session, and it is the opening bell that the terminal is
 * for.
 */

/**
 * In the order the picker lists them, which is the order they are reached
 * for: the ladder, then the straddle at a strike, then the risks. Greeks is
 * last because it is the view consulted ABOUT a position rather than the one
 * a position is opened from.
 */
export const CHAIN_MODES = ['MARKET', 'COMBINED', 'GREEKS'] as const
export type ChainMode = (typeof CHAIN_MODES)[number]

/** What the picker shows for each, in the order it shows them. */
export const CHAIN_MODE_LABEL: Record<ChainMode, string> = {
  MARKET: 'Market',
  GREEKS: 'Greeks',
  COMBINED: 'Combined',
}

/**
 * The icon beside each name in the picker.
 *
 * A lucide NAME rather than a component, so this module stays free of React
 * and can keep being imported by the pure helpers that read a mode. The
 * picker resolves it.
 */
export const CHAIN_MODE_ICON: Record<ChainMode, 'CandlestickChart' | 'Combine' | 'Sigma'> = {
  MARKET: 'CandlestickChart',
  COMBINED: 'Combine',
  GREEKS: 'Sigma',
}

export const CHAIN_MODE_HINT: Record<ChainMode, string> = {
  MARKET: 'Open interest, ITM chance, delta, IV and price',
  GREEKS: 'Gamma, theta, vega and rho in place of OI and ITM%',
  COMBINED: 'Delta, IV and price only, with the straddle premium for each strike',
}

/**
 * The class the table wears, which is what the grid templates key off.
 *
 * MARKET carries its own class rather than being the absence of the other
 * two. It used to be the empty string, which was fine while its rules were
 * the unqualified defaults — but the narrow-column metrics apply to MARKET
 * ALONE (Greeks has four more columns per side and cannot fit however the
 * padding is cut), and `:not(.tr-greeks):not(.tr-combined)` states that by
 * elimination. A fourth view would have to be excluded there too, and the
 * day someone forgot, Greeks would silently inherit a layout built for
 * twelve columns.
 */
export const chainModeClass = (mode: ChainMode): string =>
  mode === 'GREEKS' ? ' tr-greeks' : mode === 'COMBINED' ? ' tr-combined' : ' tr-market'

const isMode = (v: unknown): v is ChainMode =>
  typeof v === 'string' && (CHAIN_MODES as readonly string[]).includes(v)

/**
 * Validate a mode that came from outside this module.
 *
 * The picker hands back a string, and a URL or a restored form could hand
 * back anything at all. Checked rather than cast, because the value decides
 * which columns the ladder draws.
 */
export const parseChainMode = (v: unknown, fallback: ChainMode = 'MARKET'): ChainMode =>
  isMode(v) ? v : fallback

/** Where every session starts. See the note above `CHAIN_MODES`. */
export const DEFAULT_CHAIN_MODE: ChainMode = 'MARKET'
